import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fingerprint, Loader2, LogIn, Users, RefreshCw } from 'lucide-react';
import PinPad from '@/components/desktop/PinPad.tsx';
import HardwareCredentialHooks from '@/components/desktop/HardwareCredentialHooks';
import { getElectronAPI } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { useAuthStore } from '@/store/authStore';
import {
  canUsePlatformBiometrics,
  verifyPlatformFingerprint,
} from '@/desktop/fingerprintWebAuthn';
import type { HardwareCredType, LocalCashierPublicProfile } from '@/desktop/types';
import { useNavigate } from 'react-router-dom';
import { isCompanyAdmin, isManager } from '@/types';
import { startFreshOnThisPc } from '@/desktop/startFresh';
import { verifyDesktopCloudSessionAfterUnlock } from '@/desktop/cloudSession';

export default function UnlockPage() {
  const { t } = useTranslation();
  const api = getElectronAPI();
  const navigate = useNavigate();
  const { setAuth, clearAuth } = useAuthStore();
  const applyUnlock = useDesktopSessionStore((s) => s.applyUnlock);
  const clearLocalUnlock = useDesktopSessionStore((s) => s.clearLocalUnlock);
  const setResetPin = useDesktopSessionStore((s) => s.setResetPinAfterLogin);
  const boundCompany = useDesktopSessionStore((s) => s.boundCompanyCode);

  const [profiles, setProfiles] = useState<LocalCashierPublicProfile[]>([]);
  const [selected, setSelected] = useState<LocalCashierPublicProfile | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [fpBusy, setFpBusy] = useState(false);
  const [freshBusy, setFreshBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!api) return;
    const cfg = await api.getConfig();
    const list = await api.vault.listProfiles(cfg.boundCompanyCode || boundCompany || undefined);
    setProfiles(list);
    if (list.length === 1) setSelected(list[0]);
  }, [api, boundCompany]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goOnlineLogin = (resetPin: boolean) => {
    clearLocalUnlock();
    clearAuth();
    if (resetPin) setResetPin(true);
    navigate(resetPin ? '/login?resetPin=1' : '/login', { replace: true });
  };

  const handleStartFresh = async () => {
    if (!window.confirm(t('unlockPage.startFreshConfirm'))) {
      return;
    }
    setFreshBusy(true);
    try {
      await startFreshOnThisPc();
      navigate('/login', { replace: true });
    } catch (err) {
      setError((err as Error)?.message || t('unlockPage.resetFailed'));
    } finally {
      setFreshBusy(false);
    }
  };

  const finishUnlock = async (result: Parameters<typeof applyUnlock>[0]) => {
    const session = applyUnlock(result);
    if (session) {
      if (session.company?.code) {
        localStorage.setItem('company_code', session.company.code);
      }
      setAuth(session.user, session.company, session.role, session.canUsePOS);
      void verifyDesktopCloudSessionAfterUnlock({
        cloudApplied: Boolean(result.cloudApplied),
        serverUserId: result.profile?.serverUserId,
      });
      const dest = isCompanyAdmin(session.role) || isManager(session.role) ? '/dashboard' : '/';
      navigate(dest, { replace: true });
    } else {
      setError(t('unlockPage.profileIncomplete'));
    }
  };

  const submitPin = async () => {
    if (!api || !selected) return;
    setError('');
    setBusy(true);
    try {
      const result = await api.vault.unlockWithPin(selected.localId, pin);
      setPin('');
      await finishUnlock(result);
    } catch (err) {
      setError((err as Error)?.message || t('unlockPage.unlockFailed'));
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (selected && pin.length === (selected.pinLength || 4)) {
      void submitPin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, selected?.localId]);

  const onFingerprint = async () => {
    if (!api || !selected?.fingerprintEnabled) return;
    setError('');
    setFpBusy(true);
    try {
      const full = await api.vault.getProfile(selected.localId);
      const credId = selected.fingerprintCredentialId;
      if (!credId) {
        throw new Error(t('unlockPage.fingerprintReenroll'));
      }
      const asserted = await verifyPlatformFingerprint(credId);
      const result = await api.vault.unlockWithFingerprint(selected.localId, asserted);
      void full;
      await finishUnlock(result);
    } catch (err) {
      setError((err as Error)?.message || t('unlockPage.fingerprintFailed'));
    } finally {
      setFpBusy(false);
    }
  };

  const onHardware = useCallback(
    async (type: HardwareCredType, secretRef: string) => {
      if (!api) return;
      setError('');
      setBusy(true);
      try {
        const result = await api.vault.unlockWithHardware(type, secretRef);
        await finishUnlock(result);
      } catch (err) {
        setError((err as Error)?.message || t('unlockPage.cardNotRecognized'));
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api],
  );

  if (!api) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        {t('unlockPage.desktopOnly')}
      </div>
    );
  }

  const companySuffix = boundCompany ? ` · ${boundCompany}` : '';

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <HardwareCredentialHooks enabled onUnlock={onHardware} onError={setError} />
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 w-full max-w-md p-6 space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-50 text-primary-700 mb-2">
            <Users className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('unlockPage.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('unlockPage.subtitle', { company: companySuffix })}
          </p>
        </div>

        {profiles.length === 0 ? (
          <div className="text-center space-y-3">
            <p className="text-sm text-gray-600">{t('unlockPage.noProfiles')}</p>
            <button type="button" onClick={() => goOnlineLogin(false)} className="btn-primary w-full py-2.5">
              {t('unlockPage.onlineSetup')}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {profiles.map((p) => (
                <button
                  key={p.localId}
                  type="button"
                  onClick={() => {
                    setSelected(p);
                    setPin('');
                    setError('');
                  }}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm ${
                    selected?.localId === p.localId
                      ? 'border-primary-500 bg-primary-50 text-primary-900'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium">{p.displayName}</div>
                  <div className="text-xs text-gray-500">
                    {p.role} · {p.companyCode}
                  </div>
                </button>
              ))}
            </div>

            {selected && (
              <>
                <PinPad value={pin} onChange={setPin} maxLength={selected.pinLength || 4} disabled={busy} />
                {error && <p className="text-sm text-red-600 text-center">{error}</p>}
                <button
                  type="button"
                  disabled={busy || pin.length < (selected.pinLength || 4)}
                  onClick={() => void submitPin()}
                  className="btn-primary w-full py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('unlockPage.unlockWithPin')}
                </button>
                {selected.fingerprintEnabled && canUsePlatformBiometrics() && (
                  <button
                    type="button"
                    disabled={fpBusy}
                    onClick={() => void onFingerprint()}
                    className="w-full py-2.5 rounded-lg border border-gray-200 flex items-center justify-center gap-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                  >
                    {fpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                    {t('unlockPage.useFingerprint')}
                  </button>
                )}
              </>
            )}
          </>
        )}

        <div className="space-y-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => goOnlineLogin(true)}
            className="w-full text-sm text-primary-700 hover:text-primary-900 font-medium flex items-center justify-center gap-1 py-1"
          >
            <LogIn className="w-3.5 h-3.5" /> {t('unlockPage.forgotPin')}
          </button>
          <button
            type="button"
            onClick={() => goOnlineLogin(false)}
            className="w-full text-xs text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
          >
            {t('unlockPage.onlineEnroll')}
          </button>
          <button
            type="button"
            disabled={freshBusy}
            onClick={() => void handleStartFresh()}
            className="w-full text-xs text-amber-800 hover:text-amber-950 flex items-center justify-center gap-1 pt-1 disabled:opacity-50"
          >
            {freshBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {t('unlockPage.startFresh', { code: boundCompany || '…' })}
          </button>
        </div>
      </div>
    </div>
  );
}
